import { create } from 'zustand'

import {
  getAllItemsAvailability,
  type GetAllItemsAvailabilityOptions,
} from '@flecto/api/store'
import { type CoreItem } from '@flecto/types'

import { type ItemCart } from '../types/ItemCart'
import { useStoreDates } from './useStoreDates'
import { useStoreLocalStorage } from './useStoreLocalStorage'

export type BasketState = {
  items: ItemCart[]
  opened: boolean
  totalValue: number
  isLoadingTotal: boolean

  isInCart: (item: CoreItem) => boolean
  setBasketOpened: (opened: boolean) => void
  getBasketItem: (item: CoreItem) => ItemCart | undefined
  findItemIndex: (item: CoreItem) => number
  setQuantity: (item: CoreItem, quantity: number) => void
  addQuantity: (item: CoreItem, quantity: number) => void
  increaseQuantity: (item: CoreItem) => void
  decreaseQuantity: (item: CoreItem) => void
  removeItem: (item: CoreItem) => void
  recalculateTotalValue: () => void
  clearBasket: () => void
  setBasketItems: (items: ItemCart[]) => void
}

const { setBasketItems, getStoreDomain } = useStoreLocalStorage()

export const useStoreBasket = create<BasketState>((set, get) => ({
  items: [],
  totalValue: 0,
  isLoadingTotal: false,
  opened: false,

  setBasketItems: (items: ItemCart[]) => {
    set({ items })
    get().recalculateTotalValue()
  },

  setBasketOpened: (opened: boolean) => {
    set({ opened })
  },

  isInCart: (item: CoreItem) => {
    return Boolean(get().items.find((i) => i.item.id === item.id))
  },

  getBasketItem: (item: CoreItem) => {
    return get().items.find((i) => i.item.id === item.id)
  },

  findItemIndex: (item: CoreItem) => {
    return get().items.findIndex((i) => i.item.id === item.id)
  },

  increaseQuantity: (item: CoreItem) => {
    if (!get().isInCart(item)) {
      const newItems: ItemCart[] = [...get().items, { item, quantity: 1 }]

      set({ items: newItems, opened: true })
      get().recalculateTotalValue()
      setBasketItems(newItems)
      return
    }

    const indexItem = get().findItemIndex(item)

    const newItems = get().items.map((i, index) => {
      if (index === indexItem) {
        i.quantity++
      }

      return i
    })

    set({ items: newItems, opened: true })
    get().recalculateTotalValue()
    setBasketItems(newItems)
  },

  addQuantity: (item: CoreItem, quantity: number) => {
    if (!get().isInCart(item)) {
      const newItems: ItemCart[] = [...get().items, { item, quantity }]

      set({ items: newItems, opened: true })
      get().recalculateTotalValue()
      setBasketItems(newItems)
      return
    }

    const indexItem = get().findItemIndex(item)

    const newItems = get().items.map((i, index) => {
      if (index === indexItem) {
        i.quantity += quantity
      }

      return i
    })

    set({ items: newItems, opened: true })
    get().recalculateTotalValue()
    setBasketItems(newItems)
  },

  setQuantity: (item: CoreItem, quantity: number) => {
    if (!get().isInCart(item)) {
      const newItems: ItemCart[] = [...get().items, { item, quantity }]

      set({ items: newItems, opened: true })
      get().recalculateTotalValue()
      setBasketItems(newItems)
      return
    }

    const indexItem = get().findItemIndex(item)

    const newItems = get().items.map((i, index) => {
      if (index === indexItem) {
        i.quantity = quantity
      }

      return i
    })

    set({ items: newItems, opened: true })
    get().recalculateTotalValue()
    setBasketItems(newItems)
  },

  decreaseQuantity: (item: CoreItem) => {
    if (!get().isInCart(item)) {
      return
    }

    let shouldRemove = false
    const indexItem = get().findItemIndex(item)

    const newItems = get().items.map((i, index) => {
      if (index === indexItem) {
        i.quantity--

        if (i.quantity <= 0) {
          shouldRemove = true
        }
      }

      return i
    })

    if (shouldRemove) {
      get().removeItem(item)
    } else {
      set({ items: newItems, opened: true })
      get().recalculateTotalValue()
      setBasketItems(newItems)
    }
  },

  removeItem: (item: CoreItem) => {
    if (!get().isInCart(item)) {
      return
    }

    const indexItem = get().findItemIndex(item)

    const newItems = get().items.filter((_i, index) => index !== indexItem)

    set({ items: newItems, opened: true })
    get().recalculateTotalValue()
    setBasketItems(newItems)
  },

  recalculateTotalValue: async () => {
    try {
      set({ isLoadingTotal: true })
      set({ totalValue: 0 })

      const storeDomain = getStoreDomain() ?? ''
      const startAt = useStoreDates.getState().startAt ?? ''
      const endAt = useStoreDates.getState().endAt ?? ''
      const items = get().items

      if (items.length > 0 && storeDomain && startAt && endAt) {
        const options: GetAllItemsAvailabilityOptions = {
          items: items.map((i) => ({
            id: Number(i.item.id),
            quantity: i.quantity,
          })),
          start_at: startAt,
          end_at: endAt,
        }

        const itemsAvailability = await getAllItemsAvailability(
          storeDomain,
          options
        )

        set({
          totalValue:
            itemsAvailability.meta
              .total_suggested_booking_cost_with_vat_cents ?? 0,
        })
      }
    } finally {
      set({ isLoadingTotal: false })
    }
  },

  clearBasket: () => {
    set({ items: [], totalValue: 0, opened: false })
    setBasketItems([])
  },
}))
